// The Needs attention card (ART-324) — one component for the Management, Label and Booking lenses, at both
// scopes. All roster passes `scope="roster"` (one row per artist, the artist named on the row); the artist page
// passes `scope="artist"` (every item for that artist).
//
// AWARENESS, NOT TASKS (product owner): nothing here is checked off and there is no Confirm button — a row drops
// off on its own when its record moves on. Hold and show rows carry ONE action, Open, which opens the full show
// editor over the current page (the board's in-place modal, so closing returns here). Confirming is the agent's
// job at Ready to confirm, never this card's.
//
// Which rows exist, their kind and their ORDER are the server's (`NeedsAttentionService` — an expiring hold is
// pinned there, not here); the words are `needsAttentionCopy`'s.
import { useState, type ReactNode } from "react";
import { Card } from "@cntrl/ui";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { useOrgContextOrNull } from "../../app/orgContext.js";
import { useRosterArtists } from "../../app/hooks/useRoster.js";
import { ArtistMark } from "./ArtistMark.js";
import { RosterDealModal, type RosterDealTarget } from "./RosterDealModal.js";
import {
  attentionKindLabel,
  attentionSourceLine,
  attentionTiming,
  attentionTitle,
  inviteeCountLine,
  type AttentionItem,
} from "./needsAttentionCopy.js";
import styles from "./NeedsAttentionCard.module.scss";

function initials(name: string | null | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? parts[parts.length - 1]![0] : "")).toUpperCase() || "?";
}

export interface NeedsAttentionCardProps {
  scope: "roster" | "artist";
  items: readonly AttentionItem[] | null | undefined;
  /** The server's count before its cap. Not drawn since the card lost its subtitle (ART-332); kept on the props so
   *  the callers' reads stay one shape. */
  total?: number | null;
  /** Hold and show rows get Open (the full show editor). Off for a lens that cannot edit the schedule (label). */
  canOpenShows?: boolean;
  /** All roster: a row opens that artist's page. */
  onOpenArtist?: (bandId: string) => void;
  /** Refresh whatever the card sits on after the editor closes. */
  onEdited?: () => void;
  isLoading?: boolean;
  /** The card head's right side. Beside cards whose heads carry a "+", the artist page passes a spacer of the same
   *  size so the three titles and lists sit on one line (ART-330). */
  actions?: ReactNode;
  className?: string;
}

export function NeedsAttentionCard({
  scope,
  items,
  canOpenShows = false,
  onOpenArtist,
  onEdited,
  isLoading,
  actions,
  className,
}: NeedsAttentionCardProps) {
  const [target, setTarget] = useState<RosterDealTarget | null>(null);
  // All roster names the artist on each row; its photo id comes from the rail's read (cache-shared with the
  // shell), so the row's mark matches the rail beside it.
  const org = useOrgContextOrNull();
  const rail = useRosterArtists(scope === "roster" ? org?.orgId : undefined);
  const artistAvatar = (bandId: string) => rail.data?.find((a) => a.bandId === bandId)?.avatarMediaId ?? null;
  const rows = items ?? [];
  return (
    <>
      {/* No subtitle (ART-332): the card's title sits on the same line as Opportunities' beside it. */}
      <Card id={`needs-attention-${scope}`} title="Needs attention" actions={actions} className={className}>
        {isLoading ? (
          <p className={styles.empty}>Loading…</p>
        ) : rows.length === 0 ? (
          <p className={styles.empty}>Nothing pressing</p>
        ) : (
          <ul className={styles.list} data-scope={scope}>
            {rows.map((item) => {
              const timing = attentionTiming(item);
              const source = attentionSourceLine(item);
              const counts = inviteeCountLine(item.invitees);
              const showOpen = canOpenShows && item.showId != null
                && (item.kind === "hold" || item.kind === "show" || item.kind === "ready" || item.kind === "agent");
              const kind = <span className={styles.kind} data-kind={item.kind}>{attentionKindLabel(item)}</span>;
              const faces = (item.invitees?.length ?? 0) > 0 && (
                <span className={styles.faces}>
                  {item.invitees!.map((p) => (
                    <span key={p.personId} className={styles.face} data-status={p.status} title={p.name ?? undefined}>
                      <PersonAvatar
                        personId={p.personId}
                        avatarMediaId={p.avatarMediaId}
                        initials={initials(p.name)}
                        size="xs"
                      />
                    </span>
                  ))}
                </span>
              );
              // The source line carries the invitee count after it ("Hold by Dana · 2 of 3 in", ART-333).
              const sub = [source, counts].filter(Boolean).join(" · ");
              const body = (
                <>
                  {/* All roster (ART-334): the artist's picture, then the kind and the artist's name on one line over
                      the item; its people's faces after the text. The artist page has no artist to name, so its
                      kind sits alone above the title (ART-330). */}
                  {scope === "roster" && (
                    <ArtistMark
                      name={item.artistName}
                      bandId={item.bandId}
                      avatarMediaId={item.bandId ? artistAvatar(item.bandId) : null}
                      size="md"
                    />
                  )}
                  <span className={styles.text}>
                    {scope === "roster" ? (
                      <span className={styles.kindRow}>
                        {kind}
                        <span className={styles.artistName}>{item.artistName}</span>
                      </span>
                    ) : (
                      kind
                    )}
                    <span className={styles.title}>{attentionTitle(item)}</span>
                    {sub && <span className={styles.source}>{sub}</span>}
                    {scope === "artist" && faces}
                  </span>
                  {scope === "roster" && faces}
                  {timing && (
                    <span className={styles.when} data-urgent={timing.urgent || undefined}>{timing.text}</span>
                  )}
                </>
              );
              return (
                <li key={item.id} className={styles.row} data-scope={scope}>
                  {onOpenArtist && item.bandId ? (
                    <button type="button" className={styles.main} onClick={() => onOpenArtist(item.bandId!)}>
                      {body}
                    </button>
                  ) : (
                    <div className={styles.main}>{body}</div>
                  )}
                  {item.kind === "request" && item.offerId && item.bandId && (
                    <button
                      type="button"
                      className={styles.open}
                      data-variant="ink"
                      onClick={() => setTarget({ kind: "request", bandId: item.bandId!, offerId: item.offerId! })}
                    >
                      Details
                    </button>
                  )}
                  {showOpen && (
                    <button
                      type="button"
                      className={styles.open}
                      data-variant="ghost"
                      onClick={() => setTarget({ kind: "show", bandId: item.bandId!, showId: item.showId! })}
                    >
                      Open
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
      <RosterDealModal
        target={target}
        onClose={() => {
          setTarget(null);
          onEdited?.();
        }}
      />
    </>
  );
}
